/**
 * 단어 시험(DEV-I-020). DOM 없이 단위 테스트한다. 서버(DEV-I-019, growing-server/docs/api-word-quiz.md)의 응답만 쓴다.
 *
 * - 시험 문항은 서버가 출제한 것만 표시한다. 정답·뜻은 결과 조회(status completed, result 있음)에서만 표시한다.
 * - 결과가 stopped 이거나 result 가 null 이면 점수·정오·정답 뜻을 만들지 않는다(낸 응답만).
 * - 임시 데이터(sourceKind demo)는 시험용 예시로 표시한다. 실제 학습 콘텐츠의 시험 문항 승인이 아니다.
 * - 결과는 GR·근거 문제 결과와 합산하지 않는다.
 */
import type { ApiResult } from './transport.js';
import type { BatchOutcome } from './result-batch.js';

export type Confidence = 'sure' | 'unsure' | 'unknown';
export const CONFIDENCES: readonly { readonly value: Confidence; readonly label: string }[] = [
  { value: 'sure', label: '확실함' }, { value: 'unsure', label: '애매함' }, { value: 'unknown', label: '모름' },
];
export type QuizStatus = 'in_progress' | 'completed' | 'stopped';
export interface QuizProgress { readonly total: number; readonly answered: number; readonly remaining: number }
export interface QuizOption { readonly optionId: string; readonly order: number; readonly label: string }
export interface QuizItem {
  readonly itemId: string; readonly order: number; readonly headword: string; readonly prompt: string; readonly sourceKind: string;
  readonly options: readonly QuizOption[]; readonly answered: boolean; readonly selectedOptionId: string | null; readonly confidence: Confidence | null;
  /** 결과(completed)에서만 */
  readonly correct: boolean | null; readonly correctMeaning: string | null; readonly selectedLabel: string | null;
}
export interface Quiz { readonly quizId: string; readonly status: QuizStatus; readonly sourceKind: string; readonly startedAt: string; readonly progress: QuizProgress; readonly items: readonly QuizItem[]; readonly stopReason: string | null }
export interface QuizResultSummary { readonly total: number; readonly correct: number; readonly incorrect: number }
export interface QuizResult { readonly quiz: Quiz; readonly result: QuizResultSummary | null }
export interface QuizListRow { readonly quizId: string; readonly status: QuizStatus; readonly sourceKind: string; readonly startedAt: string; readonly progress: QuizProgress; readonly stopReason: string | null }
export interface AnswerAck { readonly quizId: string; readonly itemId: string; readonly status: QuizStatus; readonly progress: QuizProgress; readonly resultAvailable: boolean }

const str = (v: unknown): v is string => typeof v === 'string';
const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isStatus = (v: unknown): v is QuizStatus => v === 'in_progress' || v === 'completed' || v === 'stopped';
const isConf = (v: unknown): v is Confidence => v === 'sure' || v === 'unsure' || v === 'unknown';

function parseProgress(x: unknown): QuizProgress | null {
  if (x === null || typeof x !== 'object') return null;
  const p = x as Record<string, unknown>;
  return num(p['total']) && num(p['answered']) && num(p['remaining']) ? { total: p['total'], answered: p['answered'], remaining: p['remaining'] } : null;
}

function parseItem(x: unknown): QuizItem | null {
  if (x === null || typeof x !== 'object') return null;
  const i = x as Record<string, unknown>;
  if (!str(i['itemId']) || !num(i['order']) || !str(i['headword']) || !str(i['prompt']) || !str(i['sourceKind']) || !Array.isArray(i['options']) || typeof i['answered'] !== 'boolean') return null;
  const options: QuizOption[] = [];
  for (const o of i['options']) {
    const r = o as Record<string, unknown> | null;
    if (r === null || typeof r !== 'object' || !str(r['optionId']) || !num(r['order']) || !str(r['label'])) return null;
    options.push({ optionId: r['optionId'], order: r['order'], label: r['label'] });
  }
  if (options.length !== 4 || new Set(options.map((o) => o.optionId)).size !== 4) return null;
  const sel = i['selectedOptionId'];
  const conf = i['confidence'];
  const correct = i['correct'];
  return {
    itemId: i['itemId'], order: i['order'], headword: i['headword'], prompt: i['prompt'], sourceKind: i['sourceKind'], options, answered: i['answered'],
    selectedOptionId: str(sel) ? sel : null, confidence: isConf(conf) ? conf : null,
    correct: typeof correct === 'boolean' ? correct : null,
    correctMeaning: str(i['correctMeaning']) ? i['correctMeaning'] : null, selectedLabel: str(i['selectedLabel']) ? i['selectedLabel'] : null,
  };
}

/** 시험 하나(시작 201·조회·결과 공통). 문항 5개 초과·순서 중복·필드 누락이면 null */
export function parseQuiz(x: unknown): Quiz | null {
  if (x === null || typeof x !== 'object') return null;
  const q = x as Record<string, unknown>;
  const progress = parseProgress(q['progress']);
  if (!str(q['quizId']) || !isStatus(q['status']) || !str(q['sourceKind']) || !str(q['startedAt']) || progress === null || !Array.isArray(q['items'])) return null;
  if (q['items'].length === 0 || q['items'].length > 5) return null;
  const items: QuizItem[] = [];
  for (const it of q['items']) {
    const p = parseItem(it);
    if (p === null) return null;
    items.push(p);
  }
  if (new Set(items.map((i) => i.itemId)).size !== items.length) return null;
  items.sort((a, b) => a.order - b.order);
  return { quizId: q['quizId'], status: q['status'], sourceKind: q['sourceKind'], startedAt: q['startedAt'], progress, items, stopReason: str(q['stopReason']) ? q['stopReason'] : null };
}

/** 결과 응답. completed 이면서 result·문항별 정오·정답 뜻이 모두 있을 때만 점수를 인정한다(그 밖에는 result null) */
export function parseQuizResult(x: unknown): QuizResult | null {
  const quiz = parseQuiz(x);
  if (quiz === null) return null;
  const r = (x as Record<string, unknown>)['result'];
  if (quiz.status !== 'completed' || r === null || r === undefined || typeof r !== 'object') return { quiz: hideAnswers(quiz), result: null };
  const s = r as Record<string, unknown>;
  if (!num(s['total']) || !num(s['correct']) || !num(s['incorrect'])) return null;
  const complete = quiz.items.every((i) => i.answered && i.correct !== null && i.correctMeaning !== null);
  if (!complete) return { quiz: hideAnswers(quiz), result: null };
  return { quiz, result: { total: s['total'], correct: s['correct'], incorrect: s['incorrect'] } };
}

/** 이전 정오·뜻을 화면 값에 남기지 않는다 */
export function hideAnswers(q: Quiz): Quiz {
  return { ...q, items: q.items.map((i) => ({ ...i, correct: null, correctMeaning: null })) };
}

export function parseQuizList(body: Record<string, unknown>): QuizListRow[] | null {
  const l = body['quizzes'];
  if (!Array.isArray(l)) return null;
  const out: QuizListRow[] = [];
  const seen = new Set<string>();
  for (const x of l) {
    const r = x as Record<string, unknown> | null;
    const progress = r === null || typeof r !== 'object' ? null : parseProgress(r['progress']);
    if (r === null || typeof r !== 'object' || !str(r['quizId']) || !isStatus(r['status']) || !str(r['sourceKind']) || !str(r['startedAt']) || progress === null || seen.has(r['quizId'])) return null;
    seen.add(r['quizId']);
    out.push({ quizId: r['quizId'], status: r['status'], sourceKind: r['sourceKind'], startedAt: r['startedAt'], progress, stopReason: str(r['stopReason']) ? r['stopReason'] : null });
  }
  return out;
}

export function parseAck(body: Record<string, unknown>): AnswerAck | null {
  const progress = parseProgress(body['progress']);
  if (!str(body['quizId']) || !str(body['itemId']) || !isStatus(body['status']) || progress === null || typeof body['resultAvailable'] !== 'boolean' || body['accepted'] !== true) return null;
  return { quizId: body['quizId'], itemId: body['itemId'], status: body['status'], progress, resultAvailable: body['resultAvailable'] };
}

// ---------------------------------------------------------------- 조회 판정

export type QuizCause = 'network' | 'server' | 'timeout' | 'malformed' | 'quiz_mismatch';
export type QuizGetDecision<T> =
  | { readonly kind: 'ok'; readonly data: T }
  | { readonly kind: 'relogin' }
  | { readonly kind: 'denied' }
  | { readonly kind: 'notfound' }
  /** 결과 조회 409 quiz_not_finished */
  | { readonly kind: 'not_finished' }
  | { readonly kind: 'error'; readonly cause: QuizCause };

type Http = Extract<ApiResult, { kind: 'http' }>;
function pick(outcome: BatchOutcome): Http | 'timeout' | 'network' {
  if (outcome.kind === 'timeout') return 'timeout';
  const r = outcome.kind === 'early' ? outcome.result : outcome.grading;
  return r.kind === 'http' ? r : 'network';
}
function early(r: Http | 'timeout' | 'network'): QuizGetDecision<never> | null {
  if (r === 'timeout') return { kind: 'error', cause: 'timeout' };
  if (r === 'network') return { kind: 'error', cause: 'network' };
  if (r.status === 401) return { kind: 'relogin' };
  if (r.status === 403) return { kind: 'denied' };
  if (r.status === 404) return { kind: 'notfound' };
  return null;
}

export function decideQuizList(outcome: BatchOutcome): QuizGetDecision<QuizListRow[]> {
  const r = pick(outcome);
  const e = early(r);
  if (e !== null) return e.kind === 'notfound' ? { kind: 'error', cause: 'server' } : e;
  if (typeof r === 'string') return { kind: 'error', cause: 'server' };
  if (r.status !== 200) return { kind: 'error', cause: 'server' };
  const l = parseQuizList(r.body);
  return l === null ? { kind: 'error', cause: 'malformed' } : { kind: 'ok', data: l };
}

export function decideQuizGet(asked: string, outcome: BatchOutcome): QuizGetDecision<Quiz> {
  const r = pick(outcome);
  const e = early(r);
  if (e !== null) return e;
  if (typeof r === 'string') return { kind: 'error', cause: 'server' };
  if (r.status !== 200) return { kind: 'error', cause: 'server' };
  const q = parseQuiz(r.body);
  if (q === null) return { kind: 'error', cause: 'malformed' };
  if (q.quizId !== asked) return { kind: 'error', cause: 'quiz_mismatch' };
  // 조회 응답에는 정답 정보가 없다. 혹시 있어도 쓰지 않는다
  return { kind: 'ok', data: hideAnswers(q) };
}

export function decideQuizResult(asked: string, outcome: BatchOutcome): QuizGetDecision<QuizResult> {
  const r = pick(outcome);
  const e = early(r);
  if (e !== null) return e;
  if (typeof r === 'string') return { kind: 'error', cause: 'server' };
  if (r.status === 409 && r.body['error'] === 'quiz_not_finished') return { kind: 'not_finished' };
  if (r.status !== 200) return { kind: 'error', cause: 'server' };
  const q = parseQuizResult(r.body);
  if (q === null) return { kind: 'error', cause: 'malformed' };
  if (q.quiz.quizId !== asked) return { kind: 'error', cause: 'quiz_mismatch' };
  return { kind: 'ok', data: q };
}

// ---------------------------------------------------------------- 시작 응답

export type StartDecision =
  | { readonly kind: 'created'; readonly quiz: Quiz }
  | { readonly kind: 'none'; readonly reason: 'no_valid_words' | 'no_prepared_questions' }
  | { readonly kind: 'in_progress'; readonly quizId: string }
  | { readonly kind: 'relogin' }
  | { readonly kind: 'denied' }
  /** 응답을 받지 못했거나 해석할 수 없음 → 같은 키로만 다시 보낸다 */
  | { readonly kind: 'uncertain' }
  | { readonly kind: 'key_conflict' }
  | { readonly kind: 'error' };

export function decideStart(r: ApiResult): StartDecision {
  if (r.kind === 'network') return { kind: 'uncertain' };
  if (r.status === 401) return { kind: 'relogin' };
  if (r.status === 403) return { kind: 'denied' };
  if (r.status === 201) { const q = parseQuiz(r.body); return q === null ? { kind: 'uncertain' } : { kind: 'created', quiz: hideAnswers(q) }; }
  if (r.status === 200) {
    const reason = r.body['reason'];
    return r.body['quiz'] === null && (reason === 'no_valid_words' || reason === 'no_prepared_questions') ? { kind: 'none', reason } : { kind: 'uncertain' };
  }
  if (r.status === 409 && r.body['error'] === 'quiz_in_progress' && str(r.body['quizId'])) return { kind: 'in_progress', quizId: r.body['quizId'] };
  if (r.status === 409 && r.body['error'] === 'request_key_reused') return { kind: 'key_conflict' };
  if (r.status >= 500) return { kind: 'uncertain' };
  return { kind: 'error' };
}

// ---------------------------------------------------------------- 제출 응답

export type AnswerDecision =
  | { readonly kind: 'accepted'; readonly ack: AnswerAck }
  | { readonly kind: 'relogin' }
  | { readonly kind: 'denied' }
  | { readonly kind: 'notfound' }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'already_answered' }
  | { readonly kind: 'key_conflict' }
  | { readonly kind: 'rejected'; readonly error: string }
  /** 제출됐는지 모름: 같은 키·같은 내용으로만 다시 보낸다 */
  | { readonly kind: 'uncertain' };

export function decideAnswer(r: ApiResult): AnswerDecision {
  if (r.kind === 'network') return { kind: 'uncertain' };
  if (r.status === 401) return { kind: 'relogin' };
  if (r.status === 403) return { kind: 'denied' };
  if (r.status === 404) return { kind: 'notfound' };
  if (r.status === 201 || r.status === 200) { const a = parseAck(r.body); return a === null ? { kind: 'uncertain' } : { kind: 'accepted', ack: a }; }
  const e = String(r.body['error'] ?? '');
  if (r.status === 409 && e === 'quiz_stopped') return { kind: 'stopped' };
  if (r.status === 409 && e === 'already_answered') return { kind: 'already_answered' };
  if (r.status === 409 && e === 'request_key_reused') return { kind: 'key_conflict' };
  if (r.status >= 500 || r.status === 408 || r.status === 429) return { kind: 'uncertain' };
  return { kind: 'rejected', error: e };
}

/** 한 번의 제출 의도: 같은 키·같은 내용. 확정(accepted·거부)될 때까지 보존한다 */
export interface PendingAnswer {
  readonly owner: string; readonly quizId: string; readonly itemId: string; readonly optionId: string; readonly responseTimeMs: number | null; readonly key: string;
}
export const answerBody = (p: PendingAnswer): { optionId: string; responseTimeMs: number | null } => ({ optionId: p.optionId, responseTimeMs: p.responseTimeMs });

/** 학생이 아직 응답하지 않은 첫 문항(출제 순서) */
export function nextUnanswered(q: Quiz): QuizItem | null {
  return q.items.find((i) => !i.answered) ?? null;
}

/** 서버가 받은 응답을 화면 시험에 반영(낙관 표시 없음: 서버 ack를 받은 뒤에만) */
export function applyAccepted(q: Quiz, p: PendingAnswer, ack: AnswerAck): Quiz {
  return {
    ...q, status: ack.status, progress: ack.progress,
    items: q.items.map((i) => (i.itemId === p.itemId ? { ...i, answered: true, selectedOptionId: p.optionId, confidence: null } : i)),
  };
}

export const statusLabel = (s: QuizStatus): string => (s === 'in_progress' ? '푸는 중' : s === 'completed' ? '완료' : '중단됨');

/** 요청 문맥: 보낼 때와 응답 뒤가 모두 같아야 화면에 반영한다(늦은 응답 차단, 취소 여부와 별개) */
export interface QuizReqCtx { readonly gen: number; readonly me: string | null; readonly screen: object; readonly seq: number; readonly quizId: string | null }
export function quizResponseUsable(sent: QuizReqCtx, now: QuizReqCtx | null): boolean {
  return now !== null && sent.gen === now.gen && sent.me === now.me && sent.screen === now.screen && sent.seq === now.seq && sent.quizId === now.quizId;
}

export const QUIZ_CHECK_MS = 30_000;
